import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import OnboardingPasswordRules from "@/components/onboarding/onboarding-password-rules"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { identityPasswordResetPath } from "@/routes"

interface ResetPasswordProps {
  sid: string
  email: string
  /** OKS-306 — Server payload shape; mirrors
   *  `User.password_requirements_payload`. Drives the rule-hint block
   *  rendered above the new-password input. */
  password_requirements: {
    min_length: number
    rule_keys: string[]
  }
}

export default function ResetPassword({
  sid,
  email,
  password_requirements,
}: ResetPasswordProps) {
  const { t } = useTranslation()
  const passwordRulesId = "password-reset-rules"

  return (
    <AuthLayout
      title={t("password_resets.edit.title")}
      description={t("password_resets.edit.description")}
    >
      <Head title={t("password_resets.edit.title")} />
      <Form
        method="put"
        action={identityPasswordResetPath()}
        transform={(data) => ({ ...data, sid, email })}
        resetOnSuccess={["password", "password_confirmation"]}
      >
        {({ processing, errors }) => (
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="email">
                {t("password_resets.edit.email_label")}
              </Label>
              <Input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                className="mt-1 block w-full"
                readOnly
              />
              <InputError messages={errors.email} className="mt-2" />
            </div>

            <OnboardingPasswordRules
              id={passwordRulesId}
              minLength={password_requirements.min_length}
              ruleKeys={password_requirements.rule_keys}
            />

            <div className="grid gap-2">
              <Label htmlFor="password">
                {t("password_resets.edit.password_label")}
              </Label>
              <Input
                id="password"
                type="password"
                name="password"
                autoComplete="new-password"
                className="mt-1 block w-full"
                autoFocus
                placeholder={t("password_resets.edit.password_placeholder")}
                aria-describedby={[
                  passwordRulesId,
                  errors.password ? "password-reset-error-password" : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
              <InputError
                id="password-reset-error-password"
                messages={errors.password}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password_confirmation">
                {t("password_resets.edit.password_confirmation_label")}
              </Label>
              <Input
                id="password_confirmation"
                type="password"
                name="password_confirmation"
                autoComplete="new-password"
                className="mt-1 block w-full"
                placeholder={t(
                  "password_resets.edit.password_confirmation_placeholder",
                )}
              />
              <InputError
                messages={errors.password_confirmation}
                className="mt-2"
              />
            </div>

            <Button type="submit" className="mt-4 w-full" disabled={processing}>
              {processing && <Spinner />}
              {t("password_resets.edit.submit")}
            </Button>
          </div>
        )}
      </Form>
    </AuthLayout>
  )
}
